import LayersIcon from '@mui/icons-material/Layers';
import { SxProps, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { KMLFile } from '~hooks/useKMLFile/models';
import { useKMLFile } from '~hooks/useKMLFile/useKML';
import { useStores } from '~store';
import fetchAllPages from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

type MapLayersToggleProps = {
  map: google.maps.Map;
  sx?: SxProps;
};

const TRAFFIC_LAYER_ID = '_traffic';

const getLayerLabel = (kmlFile: KMLFile) => {
  if (kmlFile.name) {
    return kmlFile.name;
  }

  return kmlFile.fileName.replace(/\.[^/.]+$/, ''); // Remove file extension
};

export const MapLayersToggle = observer(({ map, sx }: MapLayersToggleProps) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [selectedLayerIds, setSelectedLayerIds] = useState<Set<string>>(new Set());
  const trafficLayerRef = useRef<google.maps.TrafficLayer | null>(null);

  const kmlLayersRef = useRef<Map<string, google.maps.KmlLayer>>(new Map());
  const [isFetchingKMLFiles, setIsFetchingKMLFiles] = useState(false);
  const { fetchKMLFiles } = useKMLFile();
  const [kmlFiles, setKmlFiles] = useState<KMLFile[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Load any KML files
  const loadKMLFiles = async (companyId: string, signal: AbortSignal) => {
    setIsFetchingKMLFiles(true);
    setKmlFiles([]);
    const files = await fetchAllPages(async (cursor, limit) => {
      try {
        return await fetchKMLFiles({ companyId, cursor, limit, signal });
      } catch {
        return undefined;
      }
    });

    if (signal.aborted) {
      return;
    }

    setKmlFiles(files);
    setIsFetchingKMLFiles(false);
  };

  useEffect(() => {
    const controller = new AbortController();

    if (companyId) {
      loadKMLFiles(companyId, controller.signal);
    } else {
      setKmlFiles([]);
      setIsFetchingKMLFiles(false);
    }

    return () => controller.abort();
  }, [companyId]);

  const layerOptions = useMemo(() => {
    return [
      { id: TRAFFIC_LAYER_ID, label: t('live_map.map_type.traffic') },
      ...kmlFiles.map((file) => ({ id: file.id, label: getLayerLabel(file) })),
    ];
  }, [kmlFiles]);

  useEffect(() => {
    if (map && !trafficLayerRef.current) {
      trafficLayerRef.current = new google.maps.TrafficLayer();
    }

    if (trafficLayerRef.current) {
      selectedLayerIds.has(TRAFFIC_LAYER_ID)
        ? trafficLayerRef.current.setMap(map)
        : trafficLayerRef.current.setMap(null);
    }

    // Create or update KML layers based on selection
    kmlFiles.forEach((file) => {
      let layer = kmlLayersRef.current.get(file.id);

      if (!layer) {
        layer = new google.maps.KmlLayer({
          url: file.fileUrl,
          preserveViewport: true,
        });
        kmlLayersRef.current.set(file.id, layer);
      }

      layer.setMap(selectedLayerIds.has(file.id) ? map : null);
    });
  }, [selectedLayerIds]);

  useEffect(() => {
    // Remove all KML layers when companyId changes
    kmlLayersRef.current.forEach((layer) => {
      layer.setMap(null);
    });
    kmlLayersRef.current.clear();

    // Only clear KML layers, preserve traffic layer selection
    setSelectedLayerIds((prev) => {
      const next = new Set<string>();
      if (prev.has(TRAFFIC_LAYER_ID)) {
        next.add(TRAFFIC_LAYER_ID);
      }
      return next;
    });
  }, [companyId]);

  const isSelected = selectedLayerIds.size > 0;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLayerToggle = (layerId: string) => {
    setSelectedLayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return next;
    });
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box sx={{ ...sx }}>
        <IconButton
          color={isSelected ? 'primary' : 'inherit'}
          onClick={handleClick}
          sx={{
            borderRadius: '0',
            backgroundColor: theme.semantic.surfaceRaised,
            '&:hover': {
              backgroundColor: theme.semantic.surfaceRaised,
            },
            border: '2px solid',
            borderColor: isSelected ? 'primary' : 'inherit',
            boxShadow: '0 2px 6px rgba(0,0,0,.3)',
            height: '40px',
            width: '40px',
          }}
        >
          <LayersIcon />
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box
            sx={{
              m: 1,
              mb: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('live_map.layers')}
            </Typography>
          </Box>
          <List sx={{ minWidth: 200 }}>
            {layerOptions.map((layer) => (
              <ListItem key={layer.id} disablePadding>
                <ListItemButton
                  onClick={() => handleLayerToggle(layer.id)}
                  dense
                  disableRipple
                >
                  <Checkbox
                    edge="start"
                    checked={selectedLayerIds.has(layer.id)}
                    tabIndex={-1}
                    sx={{
                      '&.Mui-checked': {
                        color: theme.brandV2.colors.treadOrange,
                      },
                      py: 0,
                    }}
                  />
                  <ListItemText primary={layer.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {isFetchingKMLFiles && (
            <Box
              sx={{
                mb: 2,
              }}
            >
              <LoadingSpinner loadingIndicatorSize={16} isVisible={isFetchingKMLFiles} />
            </Box>
          )}
        </Popover>
      </Box>
    </>
  );
});
